'use client';

import React from 'react';
import NextLink from 'next/link';
import { classnames } from '@/utils/classnames/classnames';
import { ChevronRightIcon as ChevronIcon } from '@heroicons/react/24/solid';
import { Link as IntlLink } from '@/i18n/routing';
import { Locale } from '@/project.config';
import { LinkProps } from './types';

// EWM-522 — in-app links go through next-intl's Link so the locale prefix
// follows the routing config: `/{locale}/` on the shared origin (localhost),
// prefix-less on a per-locale domain, absolute for a cross-domain target.
// External / protocol-relative / hash / query hrefs bypass localization and use
// next/link directly.
const isInternalHref = (href: string): boolean => href.startsWith('/') && !href.startsWith('//');

const Link = ({
  children,
  href,
  locale: localeOverride,
  openInNewTab = false,
  className = '',
  chevron = false,
  underlineOnHover = true,
  block = false,
  ...props
}: LinkProps) => {
  const linkClassName = classnames(className, {
    'w-fit': !block,
    'underline-offset-4 hover:underline': underlineOnHover,
    'flex items-center gap-1': !!chevron,
  });

  const target = openInNewTab ? '_blank' : '_self';
  const body = (
    <>
      {children}
      {chevron && <ChevronIcon width={16} />}
    </>
  );

  // External / non-localizable hrefs: hand straight to next/link untouched.
  if (!isInternalHref(href.toString())) {
    return (
      <NextLink role="link" href={href} target={target} className={linkClassName} {...props}>
        {body}
      </NextLink>
    );
  }

  return (
    <IntlLink
      role="link"
      href={href.toString()}
      {...(localeOverride ? { locale: localeOverride as Locale } : {})}
      target={target}
      className={linkClassName}
      {...props}
    >
      {body}
    </IntlLink>
  );
};

export default Link;
